import React, { useEffect, useMemo, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { supabase } from "../supabase/supabase";
import { useLeague } from "../context/LeagueContext";
import { useDraftContext } from "../context/DraftContext";
import {
  Settings,
  Calendar,
  Trophy,
  Shield,
  ChevronUp,
  ChevronDown,
  Shuffle,
  Save,
  Trash2,
  AlertTriangle,
  CheckCircle,
  HelpCircle,
  Lock
} from "lucide-react";
import ScoringSystemModal from '../components/ScoringSystemModal';
import {
  SEASON_YEAR,
  SEASON_WEEKS,
  LAST_FREE_AGENCY_WEEK,
  LAST_BID_WEEK,
  FIRST_CAPTAIN_WEEK,
  FIRST_TRIP_PLAY_WEEK,
  LAST_TRIP_PLAY_WEEK,
  TOTAL_LINEUP_WEEKS,
  FIRST_PLAYOFF_WEEK,
  PLAYOFF_TEAMS,
  PHASE_LEGEND,
  getWeekPhase,
  FIRST_FREE_AGENCY_WEEK,
  SUPER_DOG_SPREAD,
  WAIVER_BUDGET_MAX,
  WAIVER_BUDGET_STEP,
  WAIVER_MAX_CLAIMS,
} from "../utils/season";
import { useModalState } from "../hooks/useModalState";

// Shared by the timeline header and every timeline row — one declaration is
// what keeps the three columns aligned. Widths are set by the spelled-out
// headers, not by the one- or two-character values under them, so every
// header fits on a single line and they all occupy the same row height.
// Fractional columns so the headers spread across whatever width is going,
// with every value centred under its own header. The first column is widened
// because "COLLEGE FOOTBALL WEEK" is the longest label by some way — an even
// third leaves it ~109px on a 390px phone and it needs ~118px to stay on one
// line. 1.3fr gives it 128px there and stays proportional on desktop.
const TIMELINE_GRID = "grid grid-cols-[1.3fr_1fr_1fr] items-center gap-x-2 sm:gap-x-3 text-center";
const TIMELINE_HEADER = "text-[9px] font-semibold uppercase text-ink-faint whitespace-nowrap";

function LeagueRules() {
  const { leagueId } = useParams();
  const navigate = useNavigate();
  const { leagueData, members: contextMembers, isAdmin } = useLeague();
  const { draft } = useDraftContext();
  const [formState, setFormState] = useState({});
  const [draftOrder, setDraftOrder] = useState([]);
  const [error, setError] = useState("");

  const draftStarted = draft?.status === 'active' || draft?.status === 'complete' || leagueData?.draft_complete || false;
  const adminMember = contextMembers.find((m) => m.user_id === leagueData?.created_by);
  const adminName = adminMember ? `${adminMember.first_name} ${adminMember.last_name}`.trim() : "";

  const members = useMemo(() =>
    contextMembers.map((m) => ({
      uid: m.user_id,
      name: `${m.first_name} ${m.last_name}`.trim() || m.team_name,
      teamName: m.team_name,
    })),
    [contextMembers]
  );

  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showScoringModal, setShowScoringModal] = useState(false);

  const {
    showSuccessModal, showErrorModal,
    modalTitle, modalMessage,
    showSuccess, showError, closeModals: closeBaseModals,
  } = useModalState();

  const closeModals = () => {
    closeBaseModals();
    setShowDeleteModal(false);
    setShowScoringModal(false);
  };

  useEffect(() => {
    if (!leagueData) return;
    setFormState({
      name: leagueData.name,
      draftOrderType: leagueData.draft_order_type || "random",
      maxManagers: leagueData.max_managers,
      coAdminIds: leagueData.co_admin_ids || [],
    });
  }, [leagueData]);


  useEffect(() => {
    if (leagueData?.draft_order_type === "admin" && members.length > 0 && !draftStarted) {
      if (leagueData.custom_draft_order && leagueData.custom_draft_order.length > 0) {
        const orderedMembers = leagueData.custom_draft_order
          .map(uid => members.find(m => m.uid === uid))
          .filter(Boolean);

        const orderedUids = new Set(orderedMembers.map(m => m.uid));
        const missingMembers = members.filter(m => !orderedUids.has(m.uid));
        const newDraftOrder = [...orderedMembers, ...missingMembers];

        const currentMemberIds = new Set(draftOrder.map(m => m.uid));
        const newMemberIds = new Set(newDraftOrder.map(m => m.uid));
        const memberCountChanged = currentMemberIds.size !== newMemberIds.size;
        const membersChanged = [...currentMemberIds].some(id => !newMemberIds.has(id)) ||
                              [...newMemberIds].some(id => !currentMemberIds.has(id));

        if (memberCountChanged || membersChanged) {
          setDraftOrder(newDraftOrder);
        }
      } else {
        const currentMemberIds = new Set(draftOrder.map(m => m.uid));
        const newMemberIds = new Set(members.map(m => m.uid));
        const memberCountChanged = currentMemberIds.size !== newMemberIds.size;
        const membersChanged = [...currentMemberIds].some(id => !newMemberIds.has(id)) ||
                              [...newMemberIds].some(id => !currentMemberIds.has(id));

        if (memberCountChanged || membersChanged) {
          setDraftOrder([...members]);
        }
      }
    }
  }, [members, leagueData?.draft_order_type, leagueData?.custom_draft_order, draftStarted, draftOrder]);

  const isLeagueFull = members.length === leagueData?.max_managers;

  const handleInputChange = (field, value) => {
    setFormState((prev) => ({ ...prev, [field]: value }));
  };

  const handleDraftOrderChange = (fromIndex, toIndex) => {
    const newOrder = [...draftOrder];
    const [movedItem] = newOrder.splice(fromIndex, 1);
    newOrder.splice(toIndex, 0, movedItem);
    setDraftOrder(newOrder);
  };

  const saveDraftOrder = async () => {
    if (!isLeagueFull) {
      setError("League must be full before setting draft order.");
      return;
    }
    try {
      const orderUids = draftOrder.map(member => member.uid);
      const { error: updateError } = await supabase
        .from('leagues')
        .update({ custom_draft_order: orderUids })
        .eq('id', leagueId);
      if (updateError) throw updateError;
      setError("");
      showSuccess("Draft Order Saved!", "The draft order has been successfully saved.");
    } catch (error) {
      console.error("Error saving draft order:", error);
      setError("Failed to save draft order. Please try again.");
    }
  };

  const randomizeDraftOrder = () => {
    const shuffled = [...draftOrder].sort(() => Math.random() - 0.5);
    setDraftOrder(shuffled);
  };

  const handleDeleteLeague = async () => {
    try {
      await supabase.from('league_members').delete().eq('league_id', leagueId);
      await supabase.from('drafts').delete().eq('league_id', leagueId);
      const { error: deleteError } = await supabase.from('leagues').delete().eq('id', leagueId);
      if (deleteError) throw deleteError;
      showSuccess("League Deleted", "League has been permanently deleted!");
      setTimeout(() => navigate("/home"), 2000);
    } catch (error) {
      console.error("Error deleting league:", error);
      showError("Error", "Failed to delete league. Please try again.");
    }
  };

  const handleConfirmChanges = async () => {
    if (members.length > formState.maxManagers) {
      setError(`Reduce managers to ${formState.maxManagers} or fewer before changing this setting.`);
      return;
    }

    // Nothing about the draft is scheduled or timed, so there is nothing here
    // to validate and no draft_date or time_per_pick to write.
    const update = {
      name: formState.name,
      draft_order_type: formState.draftOrderType,
      max_managers: formState.maxManagers,
      scoring_type: leagueData.scoring_type
    };

    try {
      const { error: updateError } = await supabase.from('leagues').update(update).eq('id', leagueId);
      if (updateError) throw updateError;

      // co_admin_ids goes through its own RPC — it validates league membership
      // and the two-person cap server-side rather than trusting this form.
      const { data: coAdminResult, error: coAdminError } = await supabase.rpc('set_co_admins', {
        p_league_id: leagueId,
        p_user_ids:  formState.coAdminIds || [],
      });
      if (coAdminError) throw coAdminError;
      if (coAdminResult?.error) {
        setError(coAdminResult.error);
        return;
      }

      setError("");
      showSuccess("Settings Updated!", "League settings have been successfully updated.");
      setTimeout(() => window.location.reload(), 1500);
    } catch (error) {
      console.error("Error updating league:", error);
      setError("Failed to update league settings. Please try again.");
    }
  };

  const getDraftOrderTypeDisplay = (type) => {
    switch (type) {
      case "random": return "Random Order (Determined at Draft Start)";
      case "admin":  return "Commissioner Sets Order";
      default:       return type;
    }
  };

  if (!leagueData) {
    return (
      <div className="overflow-x-hidden">
        <div className="flex items-center justify-center min-h-[60vh]">
          <p className="text-lg text-danger-text">League not found.</p>
        </div>
      </div>
    );
  }

  const inputClass = "w-full px-4 py-3 bg-surface-card border border-hairline rounded-xl text-ink placeholder-gray-400 focus:border-brand focus:ring-1 focus:ring-brand focus:outline-none transition-colors disabled:opacity-50 disabled:bg-surface-subtle";
  const labelClass = "block text-sm font-medium text-ink-muted mb-2";
  const cardClass = "bg-surface-card rounded-2xl p-6 border border-hairline shadow-sm w-full";

  return (
    <div className="overflow-x-hidden">

      <div className="w-full max-w-6xl mx-auto px-4 sm:px-6 py-8 pb-40">

        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center gap-3">
            <Settings size={26} className="text-ink-faint flex-shrink-0" />
            <div>
              <h1 className="text-2xl font-black text-ink leading-tight">League Settings</h1>
              <p className="text-ink-muted text-sm mt-0.5">{leagueData?.name}</p>
            </div>
          </div>
        </div>

        {/* Draft Status Alert */}
        {draftStarted && (
          <div className={`mb-6 p-4 rounded-2xl border flex items-center gap-3 ${
            leagueData?.draft_complete ? 'bg-success-bg border-success-border' : 'bg-captain-fill border-captain-border'
          }`}>
            {leagueData?.draft_complete
              ? <CheckCircle size={20} className="text-success-text flex-shrink-0" />
              : <Lock size={20} className="text-captain-text flex-shrink-0" />
            }
            <div>
              <span className={`font-semibold text-sm ${leagueData?.draft_complete ? 'text-success-text' : 'text-captain-text'}`}>
                {leagueData?.draft_complete ? "Draft Completed. " : "Draft In Progress. "}
              </span>
              <span className={`text-sm ${leagueData?.draft_complete ? 'text-success-text' : 'text-captain-text'}`}>
                League settings are locked and cannot be changed.
              </span>
            </div>
          </div>
        )}

        {/* Error Alert */}
        {error && (
          <div className="mb-6 p-4 rounded-2xl border bg-danger-bg border-danger-border flex items-center gap-3">
            <AlertTriangle size={20} className="text-danger-text flex-shrink-0" />
            <div className="text-danger-text text-sm">{error}</div>
          </div>
        )}

        {/* ── Two-column desktop layout ── */}
        <div className="flex flex-col lg:flex-row gap-6 items-start">

          {/* ── LEFT: Config / Info ── */}
          <div className="w-full lg:w-[360px] lg:flex-shrink-0 space-y-5">

            {isAdmin ? (
              <>
                {/* League Configuration */}
                <div className={cardClass}>
                  <div className="flex items-center gap-2.5 mb-5">
                    <Settings size={20} className="text-brand flex-shrink-0" />
                    <h2 className="text-base font-bold text-ink">League Configuration</h2>
                  </div>

                  <div className="space-y-4">
                    <div>
                      <label className={labelClass}>League Name</label>
                      <input
                        value={formState.name || ''}
                        onChange={(e) => handleInputChange("name", e.target.value)}
                        disabled={draftStarted}
                        className={inputClass}
                      />
                    </div>

                    <div>
                      <label className={labelClass}>Draft Order</label>
                      <select
                        value={formState.draftOrderType || ''}
                        onChange={(e) => handleInputChange("draftOrderType", e.target.value)}
                        disabled={draftStarted}
                        className={inputClass}
                      >
                        <option value="random">Random (at draft start)</option>
                        <option value="admin">Commissioner sets order</option>
                      </select>
                    </div>

                    <div>
                      <label className={labelClass}>Who can enter picks for others</label>
                      <p className="text-xs text-ink-faint mb-2">
                        Managers pick for themselves. You can also enter a pick for a manager
                        who is not available. Choose up to two other managers who can do the same.
                      </p>
                      <div className="space-y-1.5">
                        {members
                          .filter((m) => m.uid !== leagueData.admin_id)
                          .map((m) => {
                            const selected = (formState.coAdminIds || []).includes(m.uid);
                            const atLimit  = (formState.coAdminIds || []).length >= 2;
                            return (
                              <label
                                key={m.uid}
                                className={`flex items-center gap-2.5 px-3 py-2 rounded-xl border text-sm ${
                                  selected ? 'bg-brand-bg border-hairline' : 'bg-surface-card border-hairline'
                                } ${!selected && atLimit ? 'opacity-40' : 'cursor-pointer'}`}
                              >
                                <input
                                  type="checkbox"
                                  checked={selected}
                                  disabled={!selected && atLimit}
                                  onChange={(e) => {
                                    const current = formState.coAdminIds || [];
                                    handleInputChange(
                                      "coAdminIds",
                                      e.target.checked
                                        ? [...current, m.uid]
                                        : current.filter((id) => id !== m.uid)
                                    );
                                  }}
                                />
                                <span className="font-medium text-ink">{m.name}</span>
                                <span className="text-ink-faint text-xs">{m.teamName}</span>
                              </label>
                            );
                          })}
                        {members.filter((m) => m.uid !== leagueData.admin_id).length === 0 && (
                          <p className="text-sm text-ink-faint">No other managers have joined yet.</p>
                        )}
                      </div>
                    </div>

                    <div>
                      <label className={labelClass}>Max Managers</label>
                      <select
                        value={formState.maxManagers || ''}
                        onChange={(e) => handleInputChange("maxManagers", parseInt(e.target.value))}
                        disabled={draftStarted}
                        className={inputClass}
                      >
                        {[8, 10, 12].map((num) => (
                          <option key={num} value={num}>{num} managers</option>
                        ))}
                      </select>
                    </div>
                  </div>

                  <div className="flex gap-3 mt-6">
                    <button
                      onClick={handleConfirmChanges}
                      disabled={draftStarted}
                      className="flex-1 flex items-center justify-center gap-2 px-4 py-2.5 bg-brand hover:bg-brand disabled:bg-hairline text-white font-semibold rounded-xl transition-colors disabled:cursor-not-allowed text-sm"
                    >
                      <Save size={15} />
                      {draftStarted ? "Locked" : "Save Changes"}
                    </button>
                    <button
                      onClick={() => setShowDeleteModal(true)}
                      className="flex items-center justify-center gap-2 px-4 py-2.5 bg-danger-bg hover:bg-danger-bg border border-danger-border text-danger-text font-semibold rounded-xl transition-colors text-sm"
                    >
                      <Trash2 size={15} />
                      Delete
                    </button>
                  </div>
                </div>

                {/* Draft Order Management */}
                {formState.draftOrderType === "admin" && !draftStarted && (
                  <div className={cardClass}>
                    <div className="flex items-center gap-2.5 mb-5">
                      <Trophy size={20} className="text-captain flex-shrink-0" />
                      <h2 className="text-base font-bold text-ink">Draft Order</h2>
                    </div>

                    {!isLeagueFull ? (
                      <div className="p-4 rounded-xl border bg-captain-fill border-captain-border flex items-start gap-3">
                        <AlertTriangle size={18} className="text-captain-text flex-shrink-0 mt-0.5" />
                        <div>
                          <div className="text-sm font-semibold text-captain-text">League must be full first</div>
                          <div className="text-captain-text text-xs mt-0.5">{members.length}/{leagueData.max_managers} managers joined</div>
                        </div>
                      </div>
                    ) : (
                      <>
                        <div className="flex gap-2 mb-4">
                          <button
                            onClick={randomizeDraftOrder}
                            className="flex items-center gap-1.5 px-3 py-2 bg-surface-card border border-hairline rounded-lg text-ink-muted text-sm font-medium hover:bg-surface-subtle transition-colors"
                          >
                            <Shuffle size={14} />
                            Randomize
                          </button>
                          <button
                            onClick={saveDraftOrder}
                            className="flex items-center gap-1.5 px-3 py-2 bg-success hover:bg-success text-white text-sm font-medium rounded-lg transition-colors"
                          >
                            <Save size={14} />
                            Save Order
                          </button>
                        </div>

                        <div className="space-y-2">
                          {draftOrder.map((member, index) => (
                            <div key={member.uid} className="flex items-center gap-3 p-3 bg-surface-subtle border border-hairline rounded-xl">
                              <div className="w-7 h-7 bg-brand rounded-full flex items-center justify-center text-white font-bold text-xs flex-shrink-0">
                                {index + 1}
                              </div>
                              <div className="flex-1 min-w-0">
                                <div className="text-ink font-semibold text-sm truncate">{member.name || member.username}</div>
                                <div className="text-ink-muted text-xs truncate">{member.teamName}</div>
                              </div>
                              <div className="flex flex-col gap-0.5 flex-shrink-0">
                                <button
                                  onClick={() => handleDraftOrderChange(index, Math.max(0, index - 1))}
                                  disabled={index === 0}
                                  className="p-1 bg-surface-card border border-hairline rounded text-ink-muted hover:bg-surface-subtle disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
                                >
                                  <ChevronUp size={13} />
                                </button>
                                <button
                                  onClick={() => handleDraftOrderChange(index, Math.min(draftOrder.length - 1, index + 1))}
                                  disabled={index === draftOrder.length - 1}
                                  className="p-1 bg-surface-card border border-hairline rounded text-ink-muted hover:bg-surface-subtle disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
                                >
                                  <ChevronDown size={13} />
                                </button>
                              </div>
                            </div>
                          ))}
                        </div>
                        <p className="text-xs text-ink-faint mt-3 text-center">Snake draft, round 2 reverses</p>
                      </>
                    )}
                  </div>
                )}
              </>
            ) : (
              /* Non-Admin: League Info */
              <div className={cardClass}>
                <div className="flex items-center gap-2.5 mb-5">
                  <Shield size={20} className="text-brand flex-shrink-0" />
                  <h2 className="text-base font-bold text-ink">League Information</h2>
                </div>

                <div className="space-y-0">
                  {[
                    { label: 'League Name', value: leagueData.name },
                    { label: 'Commissioner', value: adminName || 'Unknown' },
                    { label: 'Draft Order', value: getDraftOrderTypeDisplay(leagueData.draft_order_type) },
                    { label: 'Draft Start', value: 'When the commissioner starts it' },
                    { label: 'Time Per Pick', value: 'No limit' },
                  ].map(({ label, value }, i, arr) => (
                    <div key={label} className={`flex justify-between items-start py-3 gap-4 ${i < arr.length - 1 ? 'border-b border-hairline' : ''}`}>
                      <span className="text-ink-muted text-sm flex-shrink-0">{label}</span>
                      <span className="text-ink text-sm font-medium text-right">{value}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* ── RIGHT: Timeline + FAQ ── */}
          <div className="flex-1 min-w-0 space-y-5">

            {/* Season Timeline */}
            <div className={cardClass}>
              <div className="flex items-center gap-2.5 pb-4 mb-4 border-b border-hairline">
                <Calendar size={20} className="text-ink-faint flex-shrink-0" />
                <h2 className="text-base font-bold text-ink">{SEASON_YEAR} Season Timeline</h2>
              </div>

              <p className="text-xs text-ink-muted leading-relaxed mb-3">
                Lineup weeks 1-{TOTAL_LINEUP_WEEKS} line up with college football weeks 1-{TOTAL_LINEUP_WEEKS}.
                Week 0 is the only exception.
              </p>

              {/* Season-wide rules live here rather than repeating down the
                  rows, which keeps each week to date + exceptions. */}
              <div className="flex flex-wrap gap-1.5 mb-4">
                <span className="inline-flex px-2 py-0.5 rounded-full text-xs font-medium bg-surface-subtle text-ink-muted">
                  Captain required from Lineup Wk {FIRST_CAPTAIN_WEEK}
                </span>
                <span className="inline-flex px-2 py-0.5 rounded-full text-xs font-medium bg-cyan-100 text-cyan-700">
                  5x Play open Lineup Wk {FIRST_TRIP_PLAY_WEEK}-{LAST_TRIP_PLAY_WEEK}
                </span>
                <span className="inline-flex px-2 py-0.5 rounded-full text-xs font-medium bg-captain-fill text-captain-text">
                  Bidding after Lineup Wk {FIRST_FREE_AGENCY_WEEK}-{LAST_BID_WEEK}
                </span>
              </div>

              {/* Legend — the phase lives in the row colour now, so this is
                  the only place it is named. */}
              <div className="flex flex-wrap gap-x-4 gap-y-1.5 mb-4">
                {PHASE_LEGEND.map(({ key, label, dot }) => (
                  <span key={key} className="flex items-center gap-1.5 text-xs text-ink-muted">
                    <span className={`w-2.5 h-2.5 rounded-sm flex-shrink-0 ${dot}`} />
                    {label}
                  </span>
                ))}
              </div>

              {/* Three columns, same grid on the header and every row so they
                  line up. CFB week 0 is a row like any other — the CFB column
                  is what gives it somewhere to sit. */}
              <div className="-mx-6">
                <div className={`${TIMELINE_GRID} px-6 pb-2 border-b border-hairline`}>
                  <span className={TIMELINE_HEADER}>College Football Week</span>
                  <span className={TIMELINE_HEADER}>Lineup Week</span>
                  <span className={TIMELINE_HEADER}>Dates</span>
                </div>

                <div className="divide-y divide-hairline">
                  {SEASON_WEEKS.map(({ cfbWeek, lineupWeek, dates, scores }) => (
                    <div
                      key={cfbWeek}
                      className={`${TIMELINE_GRID} px-6 py-2.5 ${getWeekPhase(lineupWeek).row}`}
                    >
                      <span className="text-sm font-medium text-ink-faint">{cfbWeek}</span>
                      <span className="text-sm font-bold text-ink whitespace-nowrap">
                        {scores ? `Wk ${lineupWeek}` : <span className="text-ink-faint">—</span>}
                      </span>
                      <span className="text-xs text-ink-muted whitespace-nowrap">{dates}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {/* FAQ */}
            <div className={cardClass}>
              <div className="flex items-center gap-2.5 pb-4 mb-0 border-b border-hairline">
                <HelpCircle size={20} className="text-ink-faint flex-shrink-0" />
                <h2 className="text-base font-bold text-ink">Frequently Asked Questions</h2>
              </div>

              {/* Single column with hairline rules between entries, rather than
                  a two-up grid — one reading order, and it degrades to the same
                  layout on a phone instead of reflowing. */}
              <div className="divide-y divide-hairline [&>div]:py-4 first:[&>div]:pt-4 last:[&>div]:pb-0">

                <div>
                  <h3 className="text-sm font-semibold text-ink mb-1.5">How does the draft work?</h3>
                  <p className="text-ink-muted text-sm leading-relaxed">
                    The commissioner starts the draft from the draft room once everyone has joined. There is no scheduled start and no clock on a pick: you pick when you are on the clock, and the room can see how long the current pick has been running. If you are not available when your turn comes, the commissioner can enter your pick for you, and it is recorded as yours. That is also how a league that drafts together in one room runs, with one person entering every pick.
                  </p>
                </div>

                <div>
                  <h3 className="text-sm font-semibold text-ink mb-1.5">Do I have to set a lineup every week?</h3>
                  <p className="text-ink-muted text-sm leading-relaxed">
                    No. Your roster carries into each new week exactly as you left it, so the five teams that started last week start this week and score normally. A captain is assigned for you if you have not picked one. Setting a lineup is how you react to byes, matchups and injuries, not something you have to do to score at all.
                  </p>
                </div>

                <div>
                  <h3 className="text-sm font-semibold text-ink mb-1.5">Why doesn't Week 0 count?</h3>
                  <p className="text-ink-muted text-sm leading-relaxed">
                    College football opens with a short slate the last weekend of August, and only 8 teams play it. Paying those games would reward the schedule rather than the manager, so they award no fantasy points and there is no lineup to set. They still count as real football everywhere else: if Ohio State win on August 29 they go to 1-0, and it counts toward their record against the expected margin too, so it shows up in the records you use to judge a team. Lineup Week 1 starts the following week, and every lineup week after that matches the college football week of the same number.
                  </p>
                </div>

                <div>
                  <h3 className="text-sm font-semibold text-ink mb-1.5">How does team scoring work?</h3>
                  <p className="text-ink-muted text-sm leading-relaxed mb-2">
                    Teams earn points for their real results: winning, losing, and how they did against the margin they were expected to win or lose by. Points can go negative.
                  </p>
                  <p className="text-ink-muted text-sm leading-relaxed mb-2">
                    One exception, the Super Dog: a team expected to lose by {SUPER_DOG_SPREAD} points or more takes no penalty for losing. How close it kept the game still counts, so a heavy underdog is a real option rather than an automatic hole.
                  </p>
                  <button
                    onClick={() => setShowScoringModal(true)}
                    className="text-xs font-semibold text-brand hover:text-brand transition-colors"
                  >
                    View full scoring breakdown →
                  </button>
                </div>

                <div>
                  <h3 className="text-sm font-semibold text-ink mb-1.5">What is the captain system?</h3>
                  <p className="text-ink-muted text-sm leading-relaxed">
                    Every week from Lineup Week {FIRST_CAPTAIN_WEEK} you name one starter as your captain. They score 2x, and that applies to a bad week as well as a good one: a captain who loses badly can cost you 16. If you have not picked one by the morning of gameday, we assign your biggest favourite by expected margin, and you can change it right up until that team kicks off. You cannot move the captain onto a team whose game has already started.
                  </p>
                </div>

                <div>
                  <h3 className="text-sm font-semibold text-ink mb-1.5">What is the 5x Play?</h3>
                  <p className="text-ink-muted text-sm leading-relaxed">
                    One per season, available from Lineup Week {FIRST_TRIP_PLAY_WEEK} to Lineup Week {LAST_TRIP_PLAY_WEEK}. It goes on your captain and nowhere else, so it triples on top of the captain's double and that team scores 5x. It cannot be used in the playoffs. If you reach Lineup Week {LAST_TRIP_PLAY_WEEK} without playing it, we put it on your captain for you.
                  </p>
                </div>

                <div>
                  <h3 className="text-sm font-semibold text-ink mb-1.5">Do I have to fill all five starter slots?</h3>
                  <p className="text-ink-muted text-sm leading-relaxed">
                    No. An empty slot scores nothing, which beats starting a team you expect to lose badly, since a loss can cost you up to 8 points. Starting a team on a bye does the same thing. Your captain still has to be one of the teams you did start.
                  </p>
                </div>

                <div>
                  <h3 className="text-sm font-semibold text-ink mb-1.5">What is the Freeze Play?</h3>
                  <p className="text-ink-muted text-sm leading-relaxed">
                    You get 3 per season. A freeze locks in a team's score as it stands, and their final fantasy total will not change after that however the game ends. You can only freeze from the second half onwards, so the freeze button stays disabled through the first half and at half time until the third quarter starts. If a live game is not reporting a quarter yet, it counts as not eligible. A freeze cannot be undone.
                  </p>
                </div>

                <div>
                  <h3 className="text-sm font-semibold text-ink mb-1.5">Can I combine bonuses?</h3>
                  <p className="text-ink-muted text-sm leading-relaxed">
                    Yes. You can freeze your captain to lock in the multiplied score, and that includes a week you have played your 5x Play. You can't apply the same bonus type twice on one team.
                  </p>
                </div>

                <div>
                  <h3 className="text-sm font-semibold text-ink mb-1.5">How do the playoffs work?</h3>
                  <p className="text-ink-muted text-sm leading-relaxed mb-2">
                    The top {PLAYOFF_TEAMS} go through, in every league size, and cumulative points give way to head to head: whoever scores more that week goes on. Seeds are your regular season finishing order.
                  </p>
                  <p className="text-ink-muted text-sm leading-relaxed">
                    Lineup Week {FIRST_PLAYOFF_WEEK} is the opening round, seed 3 against seed 6 and seed 4 against seed 5, with the top two seeds on a bye. Week {FIRST_PLAYOFF_WEEK + 1} is the semi-finals, where seed 1 meets the lowest surviving seed. Week {TOTAL_LINEUP_WEEKS} is the final.
                  </p>
                </div>

                <div>
                  <h3 className="text-sm font-semibold text-ink mb-1.5">How are ties in the standings broken?</h3>
                  <p className="text-ink-muted text-sm leading-relaxed">
                    Three stages, in order. First, season points. If those match, whoever has put up the higher single week anywhere this season ranks higher. If that also matches, it comes down to what your captain scored in that week, including any multiplier. Ties are rare past the first stage and get rarer with every week played.
                  </p>
                </div>

                <div>
                  <h3 className="text-sm font-semibold text-ink mb-1.5">What happens if I miss the playoffs?</h3>
                  <p className="text-ink-muted text-sm leading-relaxed">
                    Everyone below the playoff line plays for next season's first pick. There is no bracket and no seeding: all of you keep setting lineups for the same three weeks, and whoever scores the most across them picks first next year. It is not a consolation round, it is worth winning, and in a 12-team league six of you are in it.
                  </p>
                </div>

                {/* Short labelled lists rather than the two long paragraphs
                    this replaced: read on a phone, the budget and roster rules
                    were buried mid-paragraph. The same rules, in the same
                    words where possible, sit in the Scoring and rules modal on
                    the Lineup tab. Every number comes from season.js. */}
                <div>
                  <h3 className="text-sm font-semibold text-ink mb-1.5">How do I pick up a team?</h3>
                  <p className="text-ink-muted text-sm leading-relaxed mb-2">
                    Before the season, any unowned team can be added instantly from the Teams tab, first come, first served. That board closes at the first kickoff, and weeks 1 to {FIRST_FREE_AGENCY_WEEK} are played with the roster you drafted. After that, you pick up teams through weekly waivers, then free agency. A league that drafts after the season has started keeps the instant board for 24 hours.
                  </p>

                  <div className="text-xs font-semibold text-ink-muted uppercase tracking-wide mt-3 mb-1">Each week</div>
                  <ul className="text-ink-muted text-sm leading-relaxed list-disc pl-5 space-y-1">
                    <li><span className="text-ink font-medium">Sunday 10am ET:</span> the week begins and bidding opens.</li>
                    <li><span className="text-ink font-medium">Tuesday 4am ET:</span> bidding closes.</li>
                    <li><span className="text-ink font-medium">Tuesday 6am ET:</span> every bid in the league resolves together. Free agency then opens: add any unowned team instantly, first come, first served, until 10am Sunday. Each team closes on its own when its game locks, 15 minutes before kickoff, and a team whose game has started cannot be dropped.</li>
                  </ul>

                  <div className="text-xs font-semibold text-ink-muted uppercase tracking-wide mt-3 mb-1">Bidding</div>
                  <ul className="text-ink-muted text-sm leading-relaxed list-disc pl-5 space-y-1">
                    <li>Bids are sealed. Nobody sees yours until it resolves, so a team that loses its starting quarterback on Saturday night is not won by whoever happens to be awake.</li>
                    <li>Claims are tried by rank: every manager's rank 1 claim across the league first, then every rank 2, and so on. A rival's higher-ranked claim that wins a team beats your lower-ranked one, whatever the amounts. Between claims of the same rank on the same team, the highest bid wins. If two bids are equal, the manager lower in the standings wins.</li>
                    <li>Your budget is set by your place in the standings: last place gets ${WAIVER_BUDGET_MAX}, and each place higher gets ${WAIVER_BUDGET_STEP} less. It resets every week, so unspent money does not carry over and winning a bid does not reduce next week's budget.</li>
                    <li>Your budget caps each claim, not the total: you can place up to {WAIVER_MAX_CLAIMS} claims at your full budget, and you only pay for the claims that win. Each win comes off your budget, and any claim below it that costs more than what is left is skipped. Bid your full budget on every claim and you get at most one team, the highest-ranked claim that wins. A win is likely this way but not guaranteed, since every claim can still be outbid. A $0 bid is allowed.</li>
                    <li>Your own claims are tried in the order you rank them, not by amount. Rank 1 is tried first across the whole league, then rank 2 whether or not rank 1 won. Each win comes off the budget your later claims are checked against. Reorder your claims from Waivers on the Lineup tab. You can hold up to {WAIVER_MAX_CLAIMS} claims at once.</li>
                    <li>Bidding on the same team again replaces your bid. Cancel a claim from Waivers on the Lineup tab.</li>
                  </ul>

                  <div className="text-xs font-semibold text-ink-muted uppercase tracking-wide mt-3 mb-1">Your roster</div>
                  <ul className="text-ink-muted text-sm leading-relaxed list-disc pl-5 space-y-1">
                    <li>Choose a team to drop, or none if you have an empty roster slot.</li>
                    <li>Once your empty slots are used, any lower claim without a drop is skipped. So is a claim whose team is already taken, or that would put you over budget.</li>
                  </ul>
                </div>

                <div>
                  <h3 className="text-sm font-semibold text-ink mb-1.5">When does free agency close?</h3>
                  <p className="text-ink-muted text-sm leading-relaxed">
                    The first waiver window follows Lineup Week {FIRST_FREE_AGENCY_WEEK} and the last follows Lineup Week {LAST_BID_WEEK}. It waits until week {FIRST_FREE_AGENCY_WEEK} because budgets come from the standings, and after one or two weeks the standings say very little. The last bids and free agency land in your Lineup Week {LAST_FREE_AGENCY_WEEK} roster. That is the last time a roster changes: the final week of the regular season (Lineup Week 10) and all three playoff weeks are played with the roster you finish week {LAST_FREE_AGENCY_WEEK} holding.
                  </p>
                </div>

                <div>
                  <h3 className="text-sm font-semibold text-ink mb-1.5">When does my lineup lock?</h3>
                  <p className="text-ink-muted text-sm leading-relaxed">
                    Team by team, 15 minutes before each of their kickoffs, rather than all at once at the start of the week. A team playing Thursday night locks on Thursday while the rest of your lineup stays open. That includes the captain: you cannot move it onto a team whose game has already started. When a kickoff time is still to be confirmed, that team locks from the start of its gameday.
                  </p>
                </div>

                <div>
                  <h3 className="text-sm font-semibold text-ink mb-1.5">Where does the expected margin come from?</h3>
                  <p className="text-ink-muted text-sm leading-relaxed">
                    Lineup publishes one for every game and updates it through the week as the picture changes. The margin shown on a team is the one their result is measured against. A game that never gets one is scored on the result alone, with no margin points either way.
                  </p>
                </div>

                <div>
                  <h3 className="text-sm font-semibold text-ink mb-1.5">Does the draft board change when teams are added or dropped?</h3>
                  <p className="text-ink-muted text-sm leading-relaxed">
                    No. The draft room stays exactly as the draft finished, all season, as the league's permanent record of who picked what. Adds and drops change your roster, not the board. The commissioner can correct a pick that was entered wrongly, which is the only thing that changes it.
                  </p>
                </div>

              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Scoring System Modal */}
      {showScoringModal && (
        <ScoringSystemModal onClose={() => setShowScoringModal(false)} />
      )}

      {/* Delete League Modal */}
      {showDeleteModal && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4">
          <div className="bg-surface-card rounded-2xl p-8 max-w-md w-full border border-hairline shadow-xl">
            <div className="text-center">
              <div className="w-14 h-14 bg-danger-bg rounded-full flex items-center justify-center mx-auto mb-5">
                <AlertTriangle size={28} className="text-danger-text" />
              </div>

              <h3 className="text-xl font-bold text-ink mb-3">
                Delete League "{leagueData.name}"?
              </h3>

              <div className="text-ink-muted text-left mb-6 text-sm">
                <p className="mb-3">This will permanently:</p>
                <ul className="list-disc list-inside space-y-1.5 mb-4">
                  <li>Delete the league and all its data</li>
                  <li>Remove it from all members' league lists</li>
                  <li>Delete all draft data and settings</li>
                </ul>
                <p className="text-danger-text font-semibold text-center">
                  THIS CANNOT BE UNDONE!
                </p>
              </div>

              <div className="flex space-x-3">
                <button
                  onClick={closeModals}
                  className="flex-1 px-6 py-3 bg-surface-card border border-hairline text-ink-muted font-semibold rounded-xl hover:bg-surface-subtle transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={handleDeleteLeague}
                  className="flex-1 px-6 py-3 bg-danger-fill hover:bg-danger-fill text-white font-semibold rounded-xl transition-colors"
                >
                  Delete League
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Success Modal */}
      {showSuccessModal && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4">
          <div className="bg-surface-card rounded-2xl p-8 max-w-md w-full border border-hairline shadow-xl">
            <div className="text-center">
              <div className="w-14 h-14 bg-success-bg rounded-full flex items-center justify-center mx-auto mb-5">
                <CheckCircle size={28} className="text-success-text" />
              </div>

              <h3 className="text-xl font-bold text-ink mb-2">
                {modalTitle}
              </h3>

              <p className="text-ink-muted text-sm mb-6">
                {modalMessage}
              </p>

              <button
                onClick={closeModals}
                className="w-full px-6 py-3 bg-success hover:bg-success text-white font-semibold rounded-xl transition-colors"
              >
                Great!
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Error Modal */}
      {showErrorModal && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4">
          <div className="bg-surface-card rounded-2xl p-8 max-w-md w-full border border-hairline shadow-xl">
            <div className="text-center">
              <div className="w-14 h-14 bg-danger-bg rounded-full flex items-center justify-center mx-auto mb-5">
                <AlertTriangle size={28} className="text-danger-text" />
              </div>

              <h3 className="text-xl font-bold text-ink mb-2">
                {modalTitle}
              </h3>

              <p className="text-ink-muted text-sm mb-6">
                {modalMessage}
              </p>

              <button
                onClick={closeModals}
                className="w-full px-6 py-3 bg-surface-subtle border border-hairline text-ink-muted font-semibold rounded-xl hover:bg-surface-subtle transition-colors"
              >
                Got it
              </button>
            </div>
          </div>
        </div>
      )}

    </div>
  );
}

export default LeagueRules;
